import { createClient, type Client } from "@connectrpc/connect";
import { createConnectTransport } from "@connectrpc/connect-web";
import { useMemo, useRef } from "react";

import { useCreateEnvironmentAccessToken, useEnvironment } from "@/queries/environment-queries";
import { EnvironmentOpsService } from "gitpod-next-api/supervisor/v1/environmentopts_pb";

export type EnvironmentOpsServiceClient = Client<typeof EnvironmentOpsService>;

export const useEnvironmentOpsService = (environmentId: string): EnvironmentOpsServiceClient | null => {
    const { data: environment } = useEnvironment(environmentId);
    const { data: environmentAccessToken } = useCreateEnvironmentAccessToken(environment?.id);

    // Keep the latest token in a ref so the transport interceptor always uses
    // the current value without needing to recreate the transport or client.
    const tokenRef = useRef<string | undefined>(environmentAccessToken);
    tokenRef.current = environmentAccessToken;

    const opsUrlString = environment?.status?.environmentUrls?.ops;
    return useMemo(() => {
        if (!opsUrlString || !tokenRef.current) {
            return null;
        }

        const supervisorUrl = new URL(opsUrlString);
        // Keep path for devrunner
        if (!supervisorUrl.pathname.includes("/supervisor/")) {
            // TODO: Remove this once no active orgs have runners with version between october 2025 and february 2026
            // https://github.com/gitpod-io/gitpod-next/pull/14156
            supervisorUrl.pathname = "";
        }

        const transport = createConnectTransport({
            baseUrl: supervisorUrl.toString(),
            useBinaryFormat: false,
            interceptors: [
                (next) => async (req) => {
                    req.header.set("Authorization", `Bearer ${tokenRef.current}`);
                    return await next(req);
                },
            ],
            defaultTimeoutMs: 30 * 1000,
        });

        return createClient(EnvironmentOpsService, transport);
    }, [opsUrlString]);
};
